// src/services/api_secure.js
import axios from 'axios';

// Use empty string to make requests relative to current domain (nginx will proxy /api to backend)
const API_URL = process.env.REACT_APP_API_URL || '';

// Helper function to get CSRF token from cookies
function getCSRFToken() {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') {
      return decodeURIComponent(value);
    }
  }
  return null;
}

// Helper function to check if we have an access token cookie
function hasAccessToken() {
  return document.cookie.includes('access_token=');
}

// Create axios instance with secure configuration
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Always send cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track token refresh promise to prevent multiple simultaneous refreshes
let refreshPromise = null;

// Add request interceptor for CSRF token, auth token, and error handling
api.interceptors.request.use(
  (config) => {
    // Add CSRF token to state-changing requests
    if (['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    // Add Authorization header with stored access token
    const accessToken = localStorage.getItem('jmc_token');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for token refresh and error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle rate limiting
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || '60';
      const message = `Too many requests. Please try again in ${retryAfter} seconds.`;
      
      // Dispatch custom event for UI handling
      window.dispatchEvent(new CustomEvent('rateLimited', {
        detail: { retryAfter, message }
      }));
      
      return Promise.reject({
        ...error,
        rateLimited: true,
        retryAfter: parseInt(retryAfter),
        message
      });
    }
    
    // Handle token expiration (401) - attempt refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Check if we have a refresh token before attempting
      const refreshToken = localStorage.getItem('jmc_refresh_token');
      if (!refreshToken) {
        // No refresh token, redirect to login
        authService.logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // If already refreshing, wait for it
      if (refreshPromise) {
        await refreshPromise;
        // Update the Authorization header with new token
        const newToken = localStorage.getItem('jmc_token');
        if (newToken) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      }

      // Start refresh process
      refreshPromise = authService.refreshToken()
        .then(() => {
          refreshPromise = null;
          // Update the Authorization header with new token
          const newToken = localStorage.getItem('jmc_token');
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          }
          return api(originalRequest);
        })
        .catch((refreshError) => {
          refreshPromise = null;
          // Refresh failed, redirect to login
          authService.logout();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        });

      return refreshPromise;
    }
    
    // Handle account locked (403)
    if (error.response?.status === 403) {
      const message = error.response.data?.detail || 'Account locked or access denied';
      
      if (message.includes('locked')) {
        window.dispatchEvent(new CustomEvent('accountLocked', {
          detail: { message }
        }));
      }
    }
    
    return Promise.reject(error);
  }
);

// Authentication services with enhanced security
export const authService = {
  login: async (email, password) => {
    console.log('=== API Login Started ===');
    try {
      // Clear any legacy tokens first
      console.log('1. Clearing old tokens...');
      localStorage.removeItem('jmc_token');
      localStorage.removeItem('jmc_refresh_token');
      localStorage.removeItem('jmc_user');

      console.log('2. Sending login request to:', '/api/login');
      const response = await api.post('/api/login', { email, password });
      console.log('3. Login response received:', response.data);

      // Store user info and tokens
      if (response.data) {
        // Parse JWT to get user info (if needed)
        try {
          console.log('4. Parsing access token...');
          const tokenParts = response.data.access_token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('5. Token payload:', payload);

            const userInfo = {
              email: payload.sub || email,
              role: response.data.role || payload.role,
              first_name: payload.first_name,
              last_name: payload.last_name
            };

            console.log('6. Storing user info in localStorage:', userInfo);
            localStorage.setItem('jmc_user', JSON.stringify(userInfo));

            // Store access token for API calls
            localStorage.setItem('jmc_token', response.data.access_token);
            console.log('6b. Stored access token');

            // Store refresh token for session persistence
            if (response.data.refresh_token) {
              localStorage.setItem('jmc_refresh_token', response.data.refresh_token);
              console.log('6c. Stored refresh token for session persistence');
            }

            // Verify it was stored
            const stored = localStorage.getItem('jmc_user');
            console.log('7. Verification - localStorage now contains:', stored);
          }
        } catch (e) {
          console.error('Error parsing token, using fallback:', e);
          // Fallback to basic user info
          const fallbackInfo = {
            email: email,
            role: response.data.role || 'user'
          };
          console.log('6b. Storing fallback user info:', fallbackInfo);
          localStorage.setItem('jmc_user', JSON.stringify(fallbackInfo));
          localStorage.setItem('jmc_token', response.data.access_token);
          if (response.data.refresh_token) {
            localStorage.setItem('jmc_refresh_token', response.data.refresh_token);
          }
        }
      }

      console.log('8. Login complete, returning response');
      console.log('=== API Login Complete ===\n');
      return response.data;
    } catch (error) {
      if (error.rateLimited) {
        throw error;
      }
      throw error.response ? error.response.data : { detail: 'Network error' };
    }
  },

  signup: async (email, password, confirmPassword, firstName = null, lastName = null) => {
    try {
      // Clear any legacy tokens first
      localStorage.removeItem('jmc_token');
      localStorage.removeItem('jmc_user');
      
      const response = await api.post('/api/signup', {
        email,
        password,
        confirm_password: confirmPassword,
        first_name: firstName,
        last_name: lastName
      });
      
      // Store user info
      if (response.data) {
        localStorage.setItem('jmc_user', JSON.stringify({
          email: email,
          role: response.data.role || 'user',
          first_name: firstName,
          last_name: lastName
        }));
        localStorage.setItem('jmc_token', response.data.access_token);
      }
      
      return response.data;
    } catch (error) {
      if (error.rateLimited) {
        throw error;
      }
      throw error.response ? error.response.data : { detail: 'Network error' };
    }
  },

  logout: async () => {
    try {
      // Call logout endpoint to clear server-side session
      await api.post('/api/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('jmc_user');
      localStorage.removeItem('jmc_token');
      localStorage.removeItem('jmc_refresh_token');

      // Clear cookies manually as backup
      document.cookie = 'access_token=; Max-Age=0; path=/;';
      document.cookie = 'refresh_token=; Max-Age=0; path=/;';
      document.cookie = 'csrf_token=; Max-Age=0; path=/;';
    }
  },

  refreshToken: async () => {
    try {
      // Get stored refresh token
      const refreshToken = localStorage.getItem('jmc_refresh_token');
      if (!refreshToken) {
        throw { detail: 'No refresh token available' };
      }

      const response = await api.post('/api/refresh', {
        refresh_token: refreshToken
      });

      // Update stored tokens with new ones
      if (response.data) {
        if (response.data.access_token) {
          localStorage.setItem('jmc_token', response.data.access_token);
          console.log('Token refreshed successfully');
        }
        if (response.data.refresh_token) {
          localStorage.setItem('jmc_refresh_token', response.data.refresh_token);
        }
      }

      return response.data;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error.response ? error.response.data : { detail: 'Token refresh failed' };
    }
  },

  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await api.post('/api/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { detail: 'Password change failed' };
    }
  },

  requestPasswordReset: async (email) => {
    try {
      const response = await api.post('/api/password-reset/request', { email });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { detail: 'Password reset request failed' };
    }
  },

  confirmPasswordReset: async (token, newPassword, confirmPassword) => {
    try {
      const response = await api.post('/api/password-reset/confirm', {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { detail: 'Password reset failed' };
    }
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('jmc_user');
    if (!userStr) return null;
    return JSON.parse(userStr);
  },

  isAuthenticated: () => {
    // Check for user info in localStorage as primary indicator
    // (cookies are httpOnly so we can't read them directly)
    const userInfo = localStorage.getItem('jmc_user');
    
    // Also check for legacy token for backward compatibility
    const legacyToken = localStorage.getItem('jmc_token');
    
    return !!userInfo || !!legacyToken;
  },

  // Validate password on client side (for immediate feedback)
  validatePassword: (password, email = null) => {
    const errors = [];
    
    // Length check
    if (password.length < 12) {
      errors.push('Password must be at least 12 characters long');
    }
    if (password.length > 128) {
      errors.push('Password must not exceed 128 characters');
    }
    
    // Complexity checks
    if (!/[A-Z]/.test(password)) {
      errors.push('Must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Must contain at least one lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('Must contain at least one number');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};:'",.<>/?\\|`~]/.test(password)) {
      errors.push('Must contain at least one special character');
    }
    
    // Common password check
    const commonPasswords = ['password', '123456', 'password123', 'admin', 'letmein'];
    if (commonPasswords.includes(password.toLowerCase())) {
      errors.push('This password is too common');
    }
    
    // Check for email username in password
    if (email) {
      const username = email.split('@')[0].toLowerCase();
      if (username.length > 3 && password.toLowerCase().includes(username)) {
        errors.push('Password should not contain your email username');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors,
      strength: calculatePasswordStrength(password)
    };
  }
};

// Calculate password strength (0-100)
function calculatePasswordStrength(password) {
  let strength = 0;
  
  // Length bonus
  strength += Math.min(password.length * 2, 30);
  
  // Complexity bonus
  if (/[a-z]/.test(password)) strength += 10;
  if (/[A-Z]/.test(password)) strength += 10;
  if (/\d/.test(password)) strength += 10;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 20;
  
  // Variety bonus
  const uniqueChars = new Set(password).size;
  strength += Math.min(uniqueChars, 20);
  
  return Math.min(strength, 100);
}

// Export rate limit handler for UI components
export const rateLimitHandler = {
  listeners: new Set(),
  
  addListener: (callback) => {
    const handler = (event) => callback(event.detail);
    window.addEventListener('rateLimited', handler);
    rateLimitHandler.listeners.add(handler);
    return handler;
  },
  
  removeListener: (handler) => {
    window.removeEventListener('rateLimited', handler);
    rateLimitHandler.listeners.delete(handler);
  },
  
  removeAllListeners: () => {
    rateLimitHandler.listeners.forEach(handler => {
      window.removeEventListener('rateLimited', handler);
    });
    rateLimitHandler.listeners.clear();
  }
};

// Maintenance Dashboard API
export const maintenanceService = {
  getDashboardSummary: async (date) => {
    const params = date ? `?date=${date}` : '';
    const response = await api.get(`/api/maintenance/dashboard-summary${params}`);
    return response.data;
  },

  getAssetHealth: async () => {
    const response = await api.get('/api/maintenance/asset-health');
    return response.data;
  },

  getWorkOrders: async (status, priority, limit = 50) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (priority) params.append('priority', priority);
    params.append('limit', limit);
    const response = await api.get(`/api/maintenance/work-orders?${params}`);
    return response.data;
  },

  getPMSchedule: async (status, daysAhead = 30) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('days_ahead', daysAhead);
    const response = await api.get(`/api/maintenance/pm-schedule?${params}`);
    return response.data;
  },

  getAlerts: async (status = 'active') => {
    const response = await api.get(`/api/maintenance/alerts?status=${status}`);
    return response.data;
  },

  getBreakdowns: async (days = 30, sectionId) => {
    const params = new URLSearchParams();
    params.append('days', days);
    if (sectionId) params.append('section_id', sectionId);
    const response = await api.get(`/api/maintenance/breakdowns?${params}`);
    return response.data;
  },

  getSpareParts: async (criticalOnly = false, status) => {
    const params = new URLSearchParams();
    if (criticalOnly) params.append('critical_only', 'true');
    if (status) params.append('status', status);
    const response = await api.get(`/api/maintenance/spare-parts?${params}`);
    return response.data;
  },

  getCostBreakdown: async (month) => {
    const params = month ? `?month=${month}` : '';
    const response = await api.get(`/api/maintenance/cost-breakdown${params}`);
    return response.data;
  }
};

// Export account lock handler for UI components
export const accountLockHandler = {
  listeners: new Set(),
  
  addListener: (callback) => {
    const handler = (event) => callback(event.detail);
    window.addEventListener('accountLocked', handler);
    accountLockHandler.listeners.add(handler);
    return handler;
  },
  
  removeListener: (handler) => {
    window.removeEventListener('accountLocked', handler);
    accountLockHandler.listeners.delete(handler);
  },
  
  removeAllListeners: () => {
    accountLockHandler.listeners.forEach(handler => {
      window.removeEventListener('accountLocked', handler);
    });
    accountLockHandler.listeners.clear();
  }
};

export default api;